import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import AdvancedFilters from '../components/AdvancedFilters.jsx';
import DaySlider from '../components/DaySlider.jsx';
import SimpleFilters from '../components/SimpleFilters.jsx';
import Spinner from '../../../react-shared/Spinner.jsx';
import TicketListing from '../components/TicketListing.jsx';

import { useTicketAvailabilityQuery, useTicketSearchQuery } from '../redux/apiSlice';

import doesFileExist from '../../../utils/doesFileExist.js';
import { updateLocationQuery } from '../../../utils/updateLocationQuery.js';

function App({
  filterCategories = '', isBlock = false, jsonSearch = null, jsonAvailability = null,
}) {
  const params = Object.fromEntries(new URLSearchParams(window.location.search));
  const requireEventIds = false;
  const defaultDay = 'thu_sun';

  const [dayChanged, setDayChanged] = useState(false);
  const [jsonFilesExist, setJsonFilesExist] = useState((!!jsonAvailability && !!jsonSearch) ? 'not checked' : 'not needed'); // 'not checked', 'not needed', 'found', 'not found'
  const [maxPrice, setMaxPrice] = useState(parseInt(params.maxPrice, 10) || 0);
  const [minPrice, setMinPrice] = useState(parseInt(params.minPrice, 10) || 0);
  const [requiredAccessibilityAccess, setRequiredAccessibilityAccess] = useState(params.requiredAccessibilityAccess === 'true' || false);
  const [selectedCategories, setSelectedCategories] = useState((!!params.categories && params.categories.split(',')) || []);
  const [selectedDay, setSelectedDay] = useState(params.day || defaultDay);

  const [days, setDays] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketIdsForAvailabilityCheck, setTicketIdsForAvailabilityCheck] = useState([]);

  const {
    data: ticketSearchData,
    error: ticketSearchError,
    isFetching: isFetchingTicketSearch,
    isSuccess: isSuccessTicketSearch,
  } = useTicketSearchQuery({
    ...(filterCategories ? { filter_category: filterCategories } : {}),
    ...(jsonFilesExist === 'found' ? { json_search: jsonSearch } : ''),
  }, {
    refetchOnMountOrArgChange: true,
    skip: jsonFilesExist === 'not checked',
  });

  const {
    data: ticketAvailabilityData,
    error: ticketAvailabilityError,
    isFetching: isFetchingticketAvailability,
    isSuccess: isSuccessTicketAvailability,
    refetch,
    isUninitialized,
  } = useTicketAvailabilityQuery({
    eventIds: ticketIdsForAvailabilityCheck,
    ...(jsonFilesExist === 'found' ? { json_availability: jsonAvailability } : ''),
  }, {
    refetchOnMountOrArgChange: true,
    skip: ticketIdsForAvailabilityCheck.length === 0 && jsonFilesExist === 'not checked',
  });

  // Function that collects tickets for a single day into an array
  const getTicketsForADay = (dayToSelectTicketsFor) => (
    ticketSearchData.data.map((ticket) => ({
      ...ticket,
      days: ticket.days.filter((day) => day.id === dayToSelectTicketsFor),
    })).filter((ticket) => ticket.days.length > 0)
  );

  // Function that filters all tickets of a single day against the existing filters/toggles
  const filterTickets = (ticketArray) => {
    let filteredTickets = ticketArray;

    const getPrice = (ticket) => {
      if (ticket.days && ticket.days.length > 0) return ticket.days[0].price;

      return ticket.price;
    };

    // Filter tickets by price
    filteredTickets = filteredTickets.filter(
      (ticket) => (getPrice(ticket) >= minPrice) && (getPrice(ticket) <= maxPrice),
    );

    // Filter tickets by accessibility
    if (requiredAccessibilityAccess) {
      filteredTickets = filteredTickets.filter((ticket) => ticket.accessibilityAccess === true);
    }

    // Filter by categories
    if (selectedCategories.length > 0) {
      filteredTickets = filteredTickets.filter((ticket) => selectedCategories.includes(ticket.ticketCategory));
    }

    // Finally, remove tickets with no eventId and set current tickets
    if (requireEventIds) filteredTickets = filteredTickets.filter((ticket) => ticket.days[0].eventId !== null);

    return filteredTickets;
  };

  // Check for existense of the json files
  useEffect(() => {
    const checkFiles = async () => {
      if (jsonFilesExist === 'not checked') {
        const availabilityJsonExists = await doesFileExist(jsonAvailability);
        const searchJsonExists = await doesFileExist(jsonSearch);

        if (availabilityJsonExists && searchJsonExists) {
          setJsonFilesExist('found');
        } else {
          setJsonFilesExist('not found');
        }
      }
    };

    checkFiles();
  }, []);

  // Set days
  useEffect(() => {
    if (
      isSuccessTicketSearch
      && ticketSearchData.data
      && ticketSearchData.data.length > 0
      && ticketSearchData.meta.days
      && ticketSearchData.meta.days.length > 0
    ) {
      // loop through each day
      const daysWithAvailability = ticketSearchData.meta.days.map((day) => ({
        ...day,
        soldOut:
          ticketSearchData.data
            // flat data until we have a single level deep array of variants
            .map((ticket) => ticket.days.flatMap((variant) => variant))
            .flatMap((variant) => variant)
            // filter array to only have variants for the current day that we are looping through
            .filter((variant) => variant.id === day.value)
            // filter array to exclude variants that have showLink as false (turned off in CMS or unavailable in TM)
            .filter((variant) => {
              const tmData = ticketAvailabilityData
                && ticketAvailabilityData.filter((tmVariantData) => tmVariantData.eventId === variant.eventId)[0];

              const useFallback = tmData ? tmData.useFallback : !requireEventIds || false;

              return useFallback ? variant.fallbackStatus.showLink : tmData.showLink;
            })
            // Filter array to only have stuff that matches the filters
            .filter((variant) => filterTickets([variant]).length > 0)
            .length === 0,
      }));

      if (daysWithAvailability.length > 0 && !dayChanged) {
        const daysAvailable = daysWithAvailability.filter((day) => !day.soldOut);

        setSelectedDay(daysAvailable.length > 0 ? daysAvailable[0].value : daysWithAvailability[0].value);
      }

      setDays(daysWithAvailability);
    }
  }, [
    isSuccessTicketAvailability,
    isSuccessTicketSearch,
    maxPrice,
    minPrice,
    requiredAccessibilityAccess,
    selectedCategories,
    selectedDay,
    setDays,
    ticketAvailabilityData,
    ticketSearchData,
  ]);

  // Fetch availability data on filter changes
  useEffect(() => {
    if (!isUninitialized) refetch();
  }, [selectedDay]);

  // Get all eventIds
  useEffect(() => {
    if (isSuccessTicketSearch && ticketSearchData.data && ticketSearchData.data.length > 0) {
      setTicketIdsForAvailabilityCheck([
        ...new Set(...[ticketSearchData.data
          .map((ticket) => ticket.days.flatMap((variant) => variant.eventId))
          .flatMap((ids) => ids)
          .filter((id) => id),
        ]),
      ]);
    }
  }, [isSuccessTicketSearch, setTicketIdsForAvailabilityCheck, ticketSearchData]);

  // useEffect that filters tickets
  useEffect(() => {
    if (isSuccessTicketSearch && ticketSearchData.data && ticketSearchData.data.length > 0) {
      // Filter tickets for the current day
      setTickets(filterTickets(getTicketsForADay(selectedDay)));

      // Separately set initial max price
      if (maxPrice === 0) setMaxPrice(ticketSearchData.meta.priceRange.max);
    }
  }, [
    isSuccessTicketSearch,
    maxPrice,
    minPrice,
    requiredAccessibilityAccess,
    selectedCategories,
    selectedDay,
    ticketSearchData,
  ]);

  // useEffect that manages URL
  useEffect(() => {
    if (!isBlock && isSuccessTicketSearch) {
      updateLocationQuery({
        categories: selectedCategories, minPrice, maxPrice, day: selectedDay, requiredAccessibilityAccess,
      });
    }
  }, [selectedCategories, isSuccessTicketSearch, minPrice, maxPrice, requiredAccessibilityAccess, selectedDay]);

  // Check that price range doesn't go over actual prices and min is not over max
  useEffect(() => {
    if (
      isSuccessTicketSearch
      && ticketSearchData.data
      && ticketSearchData.data.length > 0
      && maxPrice !== 0
    ) {
      const minPriceOffset = 100;

      if (maxPrice < minPriceOffset) setMaxPrice(minPriceOffset);
      if (minPrice >= maxPrice) setMinPrice(maxPrice - minPriceOffset >= 0 ? maxPrice - minPriceOffset : 0);

      const highestPrice = Math.max(...ticketSearchData.data
        .map((ticket) => ticket.days.flatMap((variant) => variant))
        .flatMap((variant) => variant)
        .map((variant) => variant.price));

      if (minPrice > highestPrice) setMinPrice(highestPrice - minPriceOffset >= 0 ? highestPrice - minPriceOffset : 0);
      if (maxPrice > highestPrice) setMaxPrice(highestPrice);
    }
  }, [isSuccessTicketSearch, maxPrice, minPrice, ticketSearchData]);

  if (ticketSearchError) return <div>{ticketSearchError.message || ticketSearchError.error}</div>;

  if (isFetchingTicketSearch || jsonFilesExist === 'not checked') return <Spinner withPadding />;

  if (isSuccessTicketSearch && ticketSearchData.meta) {
    const meta = { ...ticketSearchData.meta };
    delete meta.days;

    return (
      <div>
        <DaySlider
          days={days}
          isBlock={isBlock}
          selectedDay={selectedDay}
          setDayChanged={setDayChanged}
          setSelectedDay={setSelectedDay}
        />

        {isBlock && (
          <SimpleFilters
            meta={meta}
            requiredAccessibilityAccess={requiredAccessibilityAccess}
            setRequiredAccessibilityAccess={setRequiredAccessibilityAccess}
          />
        )}

        {!isBlock && (
          <AdvancedFilters
            meta={meta}
            maxPrice={maxPrice}
            minPrice={minPrice}
            requiredAccessibilityAccess={requiredAccessibilityAccess}
            selectedCategories={selectedCategories}
            setMaxPrice={setMaxPrice}
            setMinPrice={setMinPrice}
            setRequiredAccessibilityAccess={setRequiredAccessibilityAccess}
            setSelectedCategories={setSelectedCategories}
          />
        )}

        {isFetchingticketAvailability && <Spinner />}

        {ticketAvailabilityError && <div>{ticketAvailabilityError.message || ticketAvailabilityError.error}</div>}

        {((isSuccessTicketSearch && ticketIdsForAvailabilityCheck.length === 0) || isSuccessTicketAvailability) && (
          <TicketListing
            isBlock={isBlock}
            isFetchingticketAvailability={isFetchingticketAvailability}
            requireEventIds={requireEventIds}
            selectedDay={selectedDay}
            tickets={tickets}
            tmData={ticketAvailabilityData}
          />
        )}
      </div>
    );
  }

  return 'Sorry, something went wrong';
}

App.propTypes = {
  filterCategories: PropTypes.string,
  isBlock: PropTypes.bool,
  jsonAvailability: PropTypes.string,
  jsonSearch: PropTypes.string,
};

export default App;
